import { buildPlatformScope } from '@examflow/core';
import {
  getLearningModuleLabel,
  isPracticeAttempt,
  parseAttemptSpec,
  parseTrainingSessionSpec,
  type AttemptSpec,
  type ExamCode,
  type TcfModuleId,
  type TrainingIntensity,
} from '@examflow/learning-space';
import {
  resolveTrainingAttemptSource,
  type ResolvedTrainingAttemptSource,
} from './trainingAttemptEvidence';

const TRAINING_INTENSITY_LABELS: Record<TrainingIntensity, string> = {
  short: '短训',
  standard: '专项训练',
  full: '专项训练',
};

const DIAGNOSIS_MODULE_ID = 'diagnosis';
const DIAGNOSIS_MODULE_LABEL = '入门诊断';
const DIAGNOSIS_INTENSITY_LABEL = '入门诊断 · 分项抽样';

export interface QuizTrainingAttemptContext extends ResolvedTrainingAttemptSource {
  contentPack: ExamCode;
  moduleId: TcfModuleId;
  moduleLabel: string;
  intensity: TrainingIntensity;
  intensityLabel: string;
  reportHref: string | null;
  languageId: string | null;
  /** Paper size from AttemptSpec (diagnosis pack overrides intensity preset). */
  questionCount: number;
}

export interface QuizTrainingAttemptContextInput {
  languageId: string | null;
  examType: string | null;
  trainingPack: string | null;
  trainingModule: string | null;
  trainingIntensity: string | null;
  trainingSource: string | null;
  reviewFocus: string | null;
  nextReportHref: string | null;
  /** Prefer passing the raw search string so parseAttemptSpec can run. */
  search?: string | null;
  skillId?: string | null;
  intensity?: string | null;
}

export interface QuizActiveExamTypeInput {
  languageId: string | null;
  examType: string | null;
  trainingPack: string | null;
  fallbackExamType: string | null;
}

export function resolveQuizActiveExamType({
  languageId,
  examType,
  trainingPack,
  fallbackExamType,
}: QuizActiveExamTypeInput): string | null {
  const routeScope = buildPlatformScope({ languageId, examType });
  if (routeScope) return routeScope.examType;

  const trainingScope = buildPlatformScope({ examType: trainingPack });
  if (trainingScope) return trainingScope.examType;

  return buildPlatformScope({ examType: fallbackExamType })?.examType ?? null;
}

/**
 * Preferred path: build evidence context from a gate-ready AttemptSpec.
 */
export function buildQuizTrainingAttemptContextFromAttempt(
  attempt: AttemptSpec
): QuizTrainingAttemptContext | null {
  if (!isPracticeAttempt(attempt)) {
    return null;
  }

  const attemptSource = resolveTrainingAttemptSource({
    trainingSource: attempt.source,
    reviewFocus: attempt.kind === 'training' ? (attempt.reviewFocus ?? null) : null,
  });

  if (attemptSource.source === 'remediation' && !attemptSource.reviewFocus) {
    return null;
  }

  const intensityLabel =
    attemptSource.source === 'diagnosis'
      ? DIAGNOSIS_INTENSITY_LABEL
      : TRAINING_INTENSITY_LABELS[attempt.intensity];

  return {
    contentPack: attempt.examType,
    moduleId: attempt.skillId,
    moduleLabel: getTrainingModuleLabel(attempt.examType, attempt.skillId),
    intensity: attempt.intensity,
    intensityLabel,
    source: attemptSource.source,
    reviewFocus: attemptSource.reviewFocus,
    reportHref: sanitizeTrainingReportHref(attempt.next),
    languageId: attempt.languageId,
    questionCount: attempt.questionCount,
  };
}

/**
 * @deprecated Prefer buildQuizTrainingAttemptContextFromAttempt(launchGate.attempt).
 * Kept for unit tests and transitional call sites that still pass raw params.
 */
export function buildQuizTrainingAttemptContext(
  input: QuizTrainingAttemptContextInput
): QuizTrainingAttemptContext | null {
  const search = input.search ?? buildSearchFromLegacyInput(input);
  const attempt = parseAttemptSpec(search);
  if (attempt && isPracticeAttempt(attempt)) {
    const fromAttempt = buildQuizTrainingAttemptContextFromAttempt(attempt);
    if (fromAttempt) {
      const routeScope = buildPlatformScope({
        languageId: input.languageId,
        examType: input.examType,
      });
      if (routeScope && routeScope.examType !== fromAttempt.contentPack) {
        return null;
      }
      const packScope = input.trainingPack
        ? buildPlatformScope({ examType: input.trainingPack })
        : null;
      if (routeScope && packScope && packScope.examType !== routeScope.examType) {
        return null;
      }
      return fromAttempt;
    }
  }

  // Fallback when only legacy fields are present without a parseable AttemptSpec shape.
  const spec = parseTrainingSessionSpec(search);
  if (!spec) return null;

  const routeScope = buildPlatformScope({
    languageId: input.languageId,
    examType: input.examType,
  });
  if (routeScope && routeScope.examType !== spec.examType) {
    return null;
  }

  const packScope = input.trainingPack
    ? buildPlatformScope({ examType: input.trainingPack })
    : null;
  if (routeScope && packScope && packScope.examType !== routeScope.examType) {
    return null;
  }

  const attemptSource = resolveTrainingAttemptSource({
    trainingSource: spec.source ?? input.trainingSource,
    reviewFocus: spec.reviewFocus ?? (input.reviewFocus?.trim() || null),
  });

  if (attemptSource.source === 'remediation' && !attemptSource.reviewFocus) {
    return null;
  }

  const intensityLabel =
    attemptSource.source === 'diagnosis'
      ? DIAGNOSIS_INTENSITY_LABEL
      : TRAINING_INTENSITY_LABELS[spec.intensity];

  return {
    contentPack: spec.examType,
    moduleId: spec.skillId,
    moduleLabel: getTrainingModuleLabel(spec.examType, spec.skillId),
    intensity: spec.intensity,
    intensityLabel,
    source: attemptSource.source,
    reviewFocus: attemptSource.reviewFocus,
    reportHref: sanitizeTrainingReportHref(spec.next ?? input.nextReportHref),
    languageId: spec.languageId,
    questionCount: spec.questionCount,
  };
}

function buildSearchFromLegacyInput(input: QuizTrainingAttemptContextInput): string {
  const params = new URLSearchParams();
  if (input.languageId) params.set('languageId', input.languageId);
  if (input.examType) params.set('examType', input.examType);
  if (input.trainingPack) params.set('trainingPack', input.trainingPack);
  if (input.skillId) params.set('skillId', input.skillId);
  if (input.trainingModule) params.set('trainingModule', input.trainingModule);
  if (input.intensity) params.set('intensity', input.intensity);
  if (input.trainingIntensity) params.set('trainingIntensity', input.trainingIntensity);
  if (input.trainingSource) params.set('trainingSource', input.trainingSource);
  if (input.reviewFocus) params.set('reviewFocus', input.reviewFocus);
  if (input.nextReportHref) params.set('next', input.nextReportHref);
  return `?${params.toString()}`;
}

function getTrainingModuleLabel(examType: ExamCode, moduleId: TcfModuleId): string {
  if (moduleId === DIAGNOSIS_MODULE_ID) {
    return DIAGNOSIS_MODULE_LABEL;
  }

  return getLearningModuleLabel(examType, moduleId);
}

function sanitizeTrainingReportHref(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/diagnosis/ability') || value.startsWith('//')) {
    return null;
  }

  return value;
}
